import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ModuleHeader, { ModuleHeaderAction, ModuleHeaderBack } from "../../components/ModuleHeader/ModuleHeader";
import { getAdminOrder, refundAdminOrder } from "../../api/adminOrderApi";
import "./order-detail.scss";

const titleCase = (value) =>
  String(value || "-").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const money = (value) => `RM${Number(value || 0).toFixed(2)}`;
const dateTime = (value) => value
  ? new Intl.DateTimeFormat("en-MY", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit",
    }).format(new Date(value))
  : "-";

const totalByCode = (order, code) =>
  Number((order.totals || []).find((total) => total.code === code)?.value || 0);

const OrderDetailPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [actionMessage, setActionMessage] = useState("");

  const loadOrder = () => getAdminOrder(orderId)
      .then(setOrder)
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    loadOrder();
    // loadOrder only depends on the route id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleRefund = async () => {
    if (!window.confirm(`Mark order ${order.order_number} as refunded?`)) return;
    setRefunding(true);
    setActionMessage("");
    try {
      await refundAdminOrder(order.id);
      await loadOrder();
      setActionMessage("Order marked as refunded successfully.");
    } catch (requestError) {
      setActionMessage(requestError.message);
    } finally {
      setRefunding(false);
    }
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `SecondBite-Receipt-${order.order_number}`;

    const restoreTitle = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };

    window.addEventListener("afterprint", restoreTitle);
    window.print();
    window.setTimeout(restoreTitle, 1000);
  };

  const timeline = useMemo(() => {
    if (!order) return [];
    const history = order.history || [];
    const historyAt = (...statuses) =>
      history.find((entry) => statuses.includes(entry.status))?.created_at;
    const isAtLeast = (...statuses) => statuses.includes(order.status);

    const commonSteps = [
      { label: "Order Created", at: order.created_at, done: true },
      {
        label: "Payment Successful",
        at: order.paid_at || historyAt("payment_successful", "paid"),
        done: Boolean(order.paid_at) || isAtLeast("paid", "ready_for_pickup", "ready", "collected"),
      },
      {
        label: "Paid",
        at: historyAt("paid") || order.paid_at,
        done: Boolean(order.paid_at || historyAt("paid"))
          || isAtLeast("paid", "ready_for_pickup", "ready", "collected"),
      },
    ];

    if (["cancelled", "refunded"].includes(order.status)) {
      const legacyMovementAt = order.updated_at || order.created_at;
      const cancelledAt = historyAt("cancelled")
        || (["cancelled", "refunded"].includes(order.status) ? legacyMovementAt : null);
      const refundedAt = historyAt("refunded")
        || (order.status === "refunded" ? legacyMovementAt : null);

      return [
        ...commonSteps,
        {
          label: "Cancelled",
          at: cancelledAt,
          done: Boolean(cancelledAt) || isAtLeast("cancelled", "refunded"),
        },
        {
          label: "Refunded",
          at: refundedAt,
          done: Boolean(refundedAt) || order.status === "refunded",
        },
      ];
    }

    return [
      ...commonSteps,
      {
        label: "Ready for Pickup",
        at: order.ready_at || historyAt("ready_for_pickup", "ready"),
        done: isAtLeast("ready_for_pickup", "ready", "collected"),
      },
      {
        label: "Collected",
        at: order.completed_at || historyAt("collected"),
        done: order.status === "collected",
      },
    ];
  }, [order]);

  if (loading) return <div className="order-detail__state">Loading order details...</div>;
  if (error || !order) return <div className="order-detail__state">{error || "Order not found."}</div>;

  const subtotal = totalByCode(order, "subtotal");
  const platformFee = totalByCode(order, "platform_fee") || totalByCode(order, "commission");
  const grandTotal = totalByCode(order, "total") || Number(order.amount);
  const payout = ["cancelled", "refunded"].includes(order.status)
    ? 0
    : Math.max(0, grandTotal - platformFee);
  const cancelled = ["cancelled", "refunded"].includes(order.status);

  return (
    <main className="order-detail">
      <article className="print-receipt" aria-hidden="true">
        <header className="print-receipt__header">
          <div className="print-receipt__brand">
            <span>SB</span>
            <div>
              <strong>SecondBite</strong>
              <small>ORDER RECEIPT</small>
            </div>
          </div>
          <div className="print-receipt__document">
            <h1>Receipt</h1>
            <span className={`print-receipt__badge print-receipt__badge--${order.status}`}>
              {titleCase(order.status)}
            </span>
          </div>
        </header>

        <section className="print-receipt__reference">
          <div><span>Order Number</span><strong>{order.order_number}</strong></div>
          <div><span>Order Date</span><strong>{dateTime(order.created_at)}</strong></div>
          <div><span>Payment Method</span><strong>{titleCase(order.payment_method)}</strong></div>
        </section>

        <section className="print-receipt__parties">
          <div>
            <h2>Customer</h2>
            <strong>{order.customer}</strong>
            <span>{order.customer_phone || "-"}</span>
            <span>{order.customer_email || "-"}</span>
          </div>
          <div>
            <h2>Fulfilled By</h2>
            <strong>{order.merchant}</strong>
            <span>{order.outlet}</span>
            <span>Pickup: {order.pickup_window || "-"}</span>
          </div>
        </section>

        <section className="print-receipt__items">
          <div className="print-receipt__items-head">
            <span>Description</span><span>Qty</span><span>Unit Price</span><span>Amount</span>
          </div>
          {(order.items || []).map((item) => (
            <div className="print-receipt__item" key={`receipt-${item.id}`}>
              <strong>{item.name}</strong>
              <span>{item.quantity}</span>
              <span>{money(item.price)}</span>
              <strong>{money(item.subtotal)}</strong>
            </div>
          ))}
        </section>

        <section className="print-receipt__payment">
          <div className="print-receipt__note">
            <h2>Collection Information</h2>
            <p>Please present this receipt or your order number when collecting the order.</p>
            <span>Pickup window: <strong>{order.pickup_window || "-"}</strong></span>
          </div>
          <div className="print-receipt__totals">
            <div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
            <div><span>Platform Fee</span><strong>{money(platformFee)}</strong></div>
            <div className="total"><span>Total</span><strong>{money(grandTotal)}</strong></div>
          </div>
        </section>

        <footer className="print-receipt__footer">
          <p>Thank you for supporting local merchants and reducing food waste.</p>
          <span>This is a computer-generated receipt. No signature is required.</span>
          <small>Printed on {dateTime(new Date())}</small>
        </footer>
      </article>

      <ModuleHeader
        bleed
        title={(
          <>
            Order {order.order_number}
            <i className={`order-detail__status order-detail__status--${order.status}`}>
              {titleCase(order.status)}
            </i>
          </>
        )}
        description="Review order details, customer information, payment status, and collection progress."
        back={(
          <ModuleHeaderBack type="button" onClick={() => navigate("/orders")}>
            ← <strong>Back to Orders</strong>
          </ModuleHeaderBack>
        )}
        actions={(
          <ModuleHeaderAction type="button" onClick={handlePrint}>
            Print Receipt
          </ModuleHeaderAction>
        )}
      />

      <section className="order-detail__summary">
        <div><span>Customer</span><strong>{order.customer} / {order.customer_phone || "-"}</strong></div>
        <div><span>Vendor</span><strong>{order.merchant}</strong></div>
        <div><span>Pickup Window</span><strong>{order.pickup_window || "-"}</strong></div>
        <div><span>Total Amount</span><strong>{money(grandTotal)}</strong></div>
      </section>

      <section className="order-detail__card order-detail__timeline-card">
        <h3>Order Timeline</h3>
        <div className={`order-detail__timeline ${cancelled ? "order-detail__timeline--cancelled" : ""}`}>
          {timeline.map((step) => (
            <div className={step.done ? "is-done" : ""} key={step.label}>
              <i>✓</i>
              <strong>{step.label}</strong>
              <span>{dateTime(step.at)}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="order-detail__columns">
        <div>
          <section className="order-detail__card">
            <h3>Order Items</h3>
            <div className="order-detail__table">
              <div className="order-detail__table-head">
                <span>Item</span><span>Qty</span><span>Price</span><span>Total</span>
              </div>
              {(order.items || []).map((item) => (
                <div className="order-detail__table-row" key={item.id}>
                  <strong>{item.name}</strong>
                  <span>{item.quantity}</span>
                  <span>{money(item.price)}</span>
                  <strong>{money(item.subtotal)}</strong>
                </div>
              ))}
            </div>
            <div className="order-detail__totals">
              <div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
              <div className="grand"><span>Grand Total</span><strong>{money(grandTotal)}</strong></div>
              <div><span>Platform Fee</span><strong>{money(platformFee)}</strong></div>
              <div className="payout"><span>Payout Amount</span><strong>{money(payout)}</strong></div>
            </div>
          </section>

          <section className="order-detail__card">
            <h3>Customer &amp; Pickup</h3>
            <dl className="order-detail__details">
              <div><dt>Customer Email</dt><dd>{order.customer_email || "-"}</dd></div>
              <div><dt>Outlet</dt><dd>{order.merchant} - {order.outlet}</dd></div>
              <div><dt>Pickup Window</dt><dd>{order.pickup_window || "-"}</dd></div>
            </dl>
          </section>
        </div>

        <aside>
          <section className="order-detail__card order-detail__actions">
            <h3>Admin Actions</h3>
            <div>
              <strong>
                {order.status === "cancelled"
                  ? "Refund is available for this cancelled order."
                  : order.status === "refunded"
                    ? "This order has been refunded."
                    : "No admin action is required."}
              </strong>
              <span>
                Collection and cancellation are managed by the merchant. Admin can only mark a cancelled order as refunded.
              </span>
            </div>
            {order.status === "cancelled" && (
              <button type="button" onClick={handleRefund} disabled={refunding}>
                {refunding ? "Processing..." : "Mark as Refunded"}
              </button>
            )}
            {actionMessage && <p className="order-detail__action-message">{actionMessage}</p>}
          </section>
          <section className="order-detail__card">
            <h3>Payment</h3>
            <dl className="order-detail__details">
              <div><dt>Payment Status</dt><dd>{titleCase(order.status)}</dd></div>
              <div><dt>Payment Method</dt><dd>{titleCase(order.payment_method)}</dd></div>
              <div><dt>Order Time</dt><dd>{dateTime(order.created_at)}</dd></div>
            </dl>
          </section>
        </aside>
      </div>
    </main>
  );
};

export default OrderDetailPage;
